import React from "react";
import useSWR from "swr";

type VersionInfo = {
  version: string;
  commit: string;
  build_time: string;
  environment: string;
};

// Public ops endpoint (no Auth0 token needed). Absolute path on purpose: the
// front lives under /web, but nginx (local) and App Platform ingress (deployed)
// both route /api/... to the backend on the same origin.
const VERSION_URL = "/api/version/";

const fetcher = (url: string) =>
  fetch(url).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json() as Promise<VersionInfo>;
  });

const known = (value?: string) => value && value !== "unknown";

/**
 * Build/version badge for the footer, e.g. "v1.4.2 (38e0794) · staging".
 * Renders nothing while loading or if the endpoint is unreachable (bare
 * `next dev` without the proxy) — the footer must never break over ops info.
 */
const AppVersion = () => {
  const { data } = useSWR<VersionInfo>(VERSION_URL, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  if (!data) return null;

  const parts: string[] = [];
  if (known(data.version)) parts.push(`v${data.version}`);
  if (known(data.commit)) parts.push(`(${data.commit.slice(0, 7)})`);
  const label = parts.join(" ");
  if (!label && !known(data.environment)) return null;

  return (
    <span className="text-body-secondary small" data-testid="app-version">
      {label}
      {known(data.environment) ? `${label ? " · " : ""}${data.environment}` : ""}
    </span>
  );
};

export default React.memo(AppVersion);
